import { useState } from "react";
import { motion } from "motion/react";
import { getCurrentChapter } from "@/config/chapters";
import { STATE_COPY } from "@/config/chapterCopy";

const KLAVIYO_COMPANY_ID = "SxkG5u";
const KLAVIYO_LIST_ID = "XUYkBQ";

/**
 * Standalone Release List capture — reused inside product pages and the
 * Chapter Vials page whenever a chapter is not open for purchase.
 */
export function ReleaseListForm({ compact = false }: { compact?: boolean }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "sent" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || status === "loading") return;
    setStatus("loading");
    setError(null);
    try {
      const res = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", revision: "2024-10-15" },
          body: JSON.stringify({
            data: {
              type: "subscription",
              attributes: {
                profile: { data: { type: "profile", attributes: { email } } },
              },
              relationships: { list: { data: { type: "list", id: KLAVIYO_LIST_ID } } },
            },
          }),
        },
      );
      if (!res.ok && res.status !== 202) throw new Error(`Klaviyo error: ${res.status}`);
      setStatus("sent");
      setEmail("");
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  return (
    <div className={compact ? "" : "mx-auto max-w-md"}>
      <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="[ Your email ]"
          aria-label="Email for release notifications"
          maxLength={255}
          disabled={status === "loading"}
          className="flex-1 border border-input bg-transparent px-5 py-4 font-serif text-lg text-foreground placeholder:text-muted-foreground/70 focus:border-gold focus:outline-none disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          className="border border-gold/60 bg-gold px-8 py-4 font-display text-xs uppercase tracking-[0.24em] text-primary-foreground transition-all duration-300 ease-out hover:bg-gold-soft hover:shadow-[0_0_20px_rgba(184,164,123,0.25)] active:scale-[0.97] disabled:opacity-60 disabled:active:scale-100"
        >
          {status === "loading" ? "Sending…" : "Notify Me"}
        </button>
      </form>
      {status === "sent" ? (
        <p className="mt-4 font-serif text-lg italic text-gold">
          You are on the list. Watch for word from the workshop.
        </p>
      ) : status === "error" && error ? (
        <p className="mt-4 font-serif text-lg italic text-destructive">{error}</p>
      ) : null}
    </div>
  );
}

export function ReleaseAccess() {
  const chapter = getCurrentChapter();
  const stateCopy = STATE_COPY[chapter.state];
  const isOpen = chapter.state === "open";
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "sent" | "error">("idle");
  const [error, setError] = useState<string | null>(null);


  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || status === "loading") return;
    setStatus("loading");
    setError(null);

    try {
      const res = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            revision: "2024-10-15",
          },
          body: JSON.stringify({
            data: {
              type: "subscription",
              attributes: {
                profile: {
                  data: {
                    type: "profile",
                    attributes: { email },
                  },
                },
              },
              relationships: {
                list: { data: { type: "list", id: KLAVIYO_LIST_ID } },
              },
            },
          }),
        },
      );

      if (!res.ok && res.status !== 202) {
        throw new Error(`Klaviyo error: ${res.status}`);
      }

      setStatus("sent");
      setEmail("");
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  return (
    <section id="release-access" className="relative py-10 sm:py-28 lg:py-36">
      <div className="mx-auto max-w-3xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center"
        >
          <p className="eyebrow">The Release List</p>
          <h2 className="mt-6 text-3xl text-parchment sm:text-4xl lg:text-5xl">
            {isOpen ? (
              <>
                Be notified when <span className="italic text-gold/90">chapters open</span>
              </>
            ) : (
              <>
                The Winter chapter opens{" "}
                <span className="italic text-gold/90">to the list first</span>
              </>
            )}
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
            {stateCopy.listPrompt}
          </p>

          <div className="ornament mt-10">
            <span>✦</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-16"
        >
          <div className="relative border border-border bg-card/40 p-8 sm:p-12">
            <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-gold/10" />

            <form onSubmit={submit} className="mx-auto max-w-md">
              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="[ Your email ]"
                  aria-label="Email for release notifications"
                  maxLength={255}
                  disabled={status === "loading"}
                  className="flex-1 border border-input bg-transparent px-5 py-4 font-serif text-lg text-foreground placeholder:text-muted-foreground/70 focus:border-gold focus:outline-none disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="border border-gold/60 bg-gold px-8 py-4 font-display text-xs uppercase tracking-[0.24em] text-primary-foreground transition-all duration-300 ease-out hover:bg-gold-soft hover:shadow-[0_0_20px_rgba(184,164,123,0.25)] active:scale-[0.97] disabled:opacity-60 disabled:active:scale-100"
                >
                  {status === "loading" ? "Sending…" : "Notify Me"}
                </button>
              </div>
            </form>

            {status === "sent" ? (
              <p className="mt-6 text-center font-serif text-lg italic text-gold">
                You are on the list. Watch for word from the workshop.
              </p>
            ) : status === "error" && error ? (
              <p className="mt-6 text-center font-serif text-lg italic text-destructive">
                {error}
              </p>
            ) : (
              <p className="mt-6 text-center text-base leading-relaxed text-muted-foreground">
                For release notices, studio notes, and occasional sample
                announcements.
              </p>
            )}

            <p className="mx-auto mt-6 max-w-md text-center text-lg italic leading-relaxed text-muted-foreground">
              No weekly noise. When a chapter opens, you will know.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
